import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../../contexts/AuthContext';
import { collection, getDocs, query, where, orderBy, Timestamp } from 'firebase/firestore';
import { db } from '../../../../config/firebase';
import ExportDropdown from './ExportDropdown';

/**
 * Appointment Status Report Component
 *
 * Displays appointment details with client responsibility breakdown
 * Shows billing information, fees, charges, and payment status
 *
 * Features:
 * - Date range picker
 * - Client filter dropdown
 * - Status filter dropdown ("All")
 * - More button for additional filters
 * - Two-row header: "Appointment" and "Client Responsibility"
 * - Detailed billing columns: Billing Code, Rate Per Unit, Units, Total Fee, Status, Charge, Uninvoiced, Paid, Unpaid
 * - Export dropdown (CSV/Excel)
 */

interface AppointmentStatusData {
  dateOfService: string;
  clientName: string;
  billingCode: string;
  ratePerUnit: number;
  units: number;
  totalFee: number;
  status: 'NO CHARGE' | 'PENDING' | 'PAID' | 'UNPAID';
  charge: number | null;
  uninvoiced: number | null;
  paid: number | null;
  unpaid: number | null;
}

interface AppointmentStatusReportProps {
  onBack?: () => void;
}

const formatDateMMDDYYYY = (d: Date): string => {
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const yyyy = d.getFullYear();
  return `${mm}/${dd}/${yyyy}`;
};

const parseDateMMDDYYYY = (s: string): Date | null => {
  const parts = s.split('/');
  if (parts.length !== 3) return null;
  const m = parseInt(parts[0], 10) - 1;
  const d = parseInt(parts[1], 10);
  const y = parseInt(parts[2], 10);
  if (isNaN(m) || isNaN(d) || isNaN(y)) return null;
  return new Date(y, m, d);
};

const AppointmentStatusReport: React.FC<AppointmentStatusReportProps> = ({ onBack }) => {
  const { user } = useAuth();

  // Default date range: last 30 days
  const defaultEnd = new Date();
  const defaultStart = new Date();
  defaultStart.setDate(defaultStart.getDate() - 30);

  const [startDate, setStartDate] = useState(formatDateMMDDYYYY(defaultStart));
  const [endDate, setEndDate] = useState(formatDateMMDDYYYY(defaultEnd));
  const [clientFilter, setClientFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [data, setData] = useState<AppointmentStatusData[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAppointments = async () => {
      if (!user?.uid) return;
      setLoading(true);
      try {
        const start = parseDateMMDDYYYY(startDate);
        const end = parseDateMMDDYYYY(endDate);
        if (!start || !end) return;

        // Set end to end-of-day
        end.setHours(23, 59, 59, 999);

        const appointmentsRef = collection(db, 'appointments');
        const q = query(
          appointmentsRef,
          where('providerId', '==', user.uid),
          where('appointmentDate', '>=', Timestamp.fromDate(start)),
          where('appointmentDate', '<=', Timestamp.fromDate(end)),
          orderBy('appointmentDate', 'desc')
        );

        const snapshot = await getDocs(q);
        const rows: AppointmentStatusData[] = snapshot.docs.map((doc) => {
          const d = doc.data();
          // Parse appointment date
          let dateStr = '--';
          if (d.appointmentDate) {
            const ts = d.appointmentDate.toDate ? d.appointmentDate.toDate() : new Date(d.appointmentDate);
            dateStr = formatDateMMDDYYYY(ts);
          }

          const rate = d.rate || 0;
          const rawStatus = (d.status || '').toLowerCase();
          let billingStatus: AppointmentStatusData['status'] = 'NO CHARGE';
          if (rawStatus === 'completed') billingStatus = 'NO CHARGE';
          else if (rawStatus === 'cancelled' || rawStatus === 'canceled') billingStatus = 'NO CHARGE';
          else if (rawStatus === 'pending' || rawStatus === 'scheduled' || rawStatus === 'confirmed') billingStatus = 'PENDING';

          return {
            dateOfService: dateStr,
            clientName: d.patientName || 'Unknown',
            billingCode: d.billingCode || d.sessionType || '90834',
            ratePerUnit: rate,
            units: 1,
            totalFee: rate,
            status: billingStatus,
            charge: null,
            uninvoiced: null,
            paid: null,
            unpaid: null,
          };
        });

        setData(rows);
      } catch (err) {
        console.error('Error fetching appointments for report:', err);
        setData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchAppointments();
  }, [user?.uid, startDate, endDate]);

  return (
    <div style={{
      backgroundColor: '#ffffff',
      minHeight: '100vh',
      padding: '1rem'
    }}>
      {/* Breadcrumb */}
      <div style={{
        fontSize: '0.875rem',
        color: '#d1d5db',
        marginBottom: '1rem'
      }}>
        <button
          onClick={onBack}
          style={{
            background: 'none',
            border: 'none',
            color: '#d1d5db',
            textDecoration: 'none',
            cursor: 'pointer',
            padding: 0,
            font: 'inherit'
          }}
        >
          Analytics
        </button>
        {' / '}
        <span style={{ color: '#111827' }}>Appointment status</span>
      </div>

      {/* Page Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '1.5rem'
      }}>
        <h1 style={{
          fontSize: '1.875rem',
          fontWeight: '700',
          color: '#111827',
          margin: 0
        }}>
          Appointment status
        </h1>
        <ExportDropdown />
      </div>

      {/* Filters */}
      <div style={{
        display: 'flex',
        gap: '1rem',
        marginBottom: '1.5rem',
        alignItems: 'center'
      }}>
        {/* Date Range Picker */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          border: '1px solid #d1d5db',
          borderRadius: '4px',
          padding: '0.5rem 0.75rem',
          backgroundColor: '#ffffff'
        }}>
          <span style={{ fontSize: '1rem', color: '#2563eb' }}>📅</span>
          <input
            type="text"
            value={`${startDate} - ${endDate}`}
            readOnly
            style={{
              border: 'none',
              outline: 'none',
              fontSize: '0.875rem',
              color: '#111827',
              width: '180px',
              cursor: 'pointer'
            }}
          />
        </div>

        {/* Client Filter */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem'
        }}>
          <span style={{ fontSize: '1rem', color: '#d1d5db' }}>👤</span>
          <select
            value={clientFilter}
            onChange={(e) => setClientFilter(e.target.value)}
            style={{
              border: '1px solid #d1d5db',
              borderRadius: '4px',
              padding: '0.5rem 0.75rem',
              fontSize: '0.875rem',
              color: '#111827',
              backgroundColor: '#ffffff',
              cursor: 'pointer'
            }}
          >
            <option value="all">All clients</option>
            {Array.from(new Set(data.map(d => d.clientName))).sort().map(name => (
              <option key={name} value={name}>{name}</option>
            ))}
          </select>
        </div>

        {/* Status Filter */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem'
        }}>
          <span style={{ fontSize: '1rem', color: '#d1d5db' }}>☰</span>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            style={{
              border: '1px solid #d1d5db',
              borderRadius: '4px',
              padding: '0.5rem 0.75rem',
              fontSize: '0.875rem',
              color: '#111827',
              backgroundColor: '#ffffff',
              cursor: 'pointer'
            }}
          >
            <option value="all">All</option>
            <option value="no-charge">No Charge</option>
            <option value="pending">Pending</option>
            <option value="paid">Paid</option>
            <option value="unpaid">Unpaid</option>
          </select>
        </div>

        {/* More Button */}
        <button style={{
          backgroundColor: '#ffffff',
          border: '1px solid #d1d5db',
          borderRadius: '4px',
          padding: '0.5rem 0.75rem',
          fontSize: '0.875rem',
          color: '#374151',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '0.25rem'
        }}>
          ⋯ More
        </button>
      </div>

      {/* Table */}
      <div style={{
        border: '1px solid #e5e7eb',
        borderRadius: '6px',
        overflow: 'hidden',
        overflowX: 'auto'
      }}>
        <table style={{
          width: '100%',
          borderCollapse: 'collapse',
          minWidth: '1200px'
        }}>
          <thead>
            {/* First Header Row - Section Labels */}
            <tr style={{
              backgroundColor: '#f9fafb',
              borderBottom: '1px solid #e5e7eb'
            }}>
              <th colSpan={6} style={{
                padding: '0.75rem 1rem',
                textAlign: 'left',
                fontSize: '0.875rem',
                fontWeight: '600',
                color: '#374151',
                borderRight: '1px solid #e5e7eb'
              }}>
                Appointment
              </th>
              <th colSpan={4} style={{
                padding: '0.75rem 1rem',
                textAlign: 'left',
                fontSize: '0.875rem',
                fontWeight: '600',
                color: '#374151'
              }}>
                Client Responsibility
              </th>
            </tr>
            {/* Second Header Row - Column Names */}
            <tr style={{
              backgroundColor: '#f9fafb',
              borderBottom: '1px solid #e5e7eb'
            }}>
              <th style={{
                padding: '0.75rem 1rem',
                textAlign: 'left',
                fontSize: '0.75rem',
                fontWeight: '600',
                color: '#374151',
                textTransform: 'uppercase',
                letterSpacing: '0.05em'
              }}>
                Date Of Service
              </th>
              <th style={{
                padding: '0.75rem 1rem',
                textAlign: 'left',
                fontSize: '0.75rem',
                fontWeight: '600',
                color: '#374151',
                textTransform: 'uppercase',
                letterSpacing: '0.05em'
              }}>
                Client
              </th>
              <th style={{
                padding: '0.75rem 1rem',
                textAlign: 'left',
                fontSize: '0.75rem',
                fontWeight: '600',
                color: '#374151',
                textTransform: 'uppercase',
                letterSpacing: '0.05em'
              }}>
                Billing Code
              </th>
              <th style={{
                padding: '0.75rem 1rem',
                textAlign: 'right',
                fontSize: '0.75rem',
                fontWeight: '600',
                color: '#374151',
                textTransform: 'uppercase',
                letterSpacing: '0.05em'
              }}>
                Rate Per Unit
              </th>
              <th style={{
                padding: '0.75rem 1rem',
                textAlign: 'right',
                fontSize: '0.75rem',
                fontWeight: '600',
                color: '#374151',
                textTransform: 'uppercase',
                letterSpacing: '0.05em'
              }}>
                Units
              </th>
              <th style={{
                padding: '0.75rem 1rem',
                textAlign: 'right',
                fontSize: '0.75rem',
                fontWeight: '600',
                color: '#374151',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                borderRight: '1px solid #e5e7eb'
              }}>
                Total Fee
              </th>
              <th style={{
                padding: '0.75rem 1rem',
                textAlign: 'center',
                fontSize: '0.75rem',
                fontWeight: '600',
                color: '#374151',
                textTransform: 'uppercase',
                letterSpacing: '0.05em'
              }}>
                Status
              </th>
              <th style={{
                padding: '0.75rem 1rem',
                textAlign: 'right',
                fontSize: '0.75rem',
                fontWeight: '600',
                color: '#374151',
                textTransform: 'uppercase',
                letterSpacing: '0.05em'
              }}>
                Charge
              </th>
              <th style={{
                padding: '0.75rem 1rem',
                textAlign: 'right',
                fontSize: '0.75rem',
                fontWeight: '600',
                color: '#374151',
                textTransform: 'uppercase',
                letterSpacing: '0.05em'
              }}>
                Uninvoiced
              </th>
              <th style={{
                padding: '0.75rem 1rem',
                textAlign: 'right',
                fontSize: '0.75rem',
                fontWeight: '600',
                color: '#374151',
                textTransform: 'uppercase',
                letterSpacing: '0.05em'
              }}>
                Paid
              </th>
              <th style={{
                padding: '0.75rem 1rem',
                textAlign: 'right',
                fontSize: '0.75rem',
                fontWeight: '600',
                color: '#374151',
                textTransform: 'uppercase',
                letterSpacing: '0.05em'
              }}>
                Unpaid
              </th>
            </tr>
          </thead>
          <tbody>
            {/* Loading State */}
            {loading && (
              <tr>
                <td colSpan={11} style={{
                  padding: '2rem',
                  textAlign: 'center',
                  fontSize: '0.875rem',
                  color: '#6b7280'
                }}>
                  Loading appointments...
                </td>
              </tr>
            )}
            {/* Data Rows */}
            {!loading && data
              .filter(item => clientFilter === 'all' || item.clientName === clientFilter)
              .filter(item => {
                if (statusFilter === 'all') return true;
                const map: Record<string, string> = { 'no-charge': 'NO CHARGE', 'pending': 'PENDING', 'paid': 'PAID', 'unpaid': 'UNPAID' };
                return item.status === map[statusFilter];
              })
              .map((item, index, arr) => (
              <tr key={index} style={{
                borderBottom: index < arr.length - 1 ? '1px solid #e5e7eb' : 'none',
                backgroundColor: '#ffffff'
              }}>
                <td style={{
                  padding: '0.75rem 1rem',
                  fontSize: '0.875rem'
                }}>
                  <span style={{
                    color: '#2563eb',
                    cursor: 'pointer'
                  }}>
                    {item.dateOfService}
                  </span>
                </td>
                <td style={{
                  padding: '0.75rem 1rem',
                  fontSize: '0.875rem'
                }}>
                  <span style={{
                    color: '#2563eb',
                    cursor: 'pointer'
                  }}>
                    {item.clientName}
                  </span>
                </td>
                <td style={{
                  padding: '0.75rem 1rem',
                  fontSize: '0.875rem',
                  color: '#111827'
                }}>
                  {item.billingCode}
                </td>
                <td style={{
                  padding: '0.75rem 1rem',
                  textAlign: 'right',
                  fontSize: '0.875rem',
                  color: '#111827'
                }}>
                  ${item.ratePerUnit}
                </td>
                <td style={{
                  padding: '0.75rem 1rem',
                  textAlign: 'right',
                  fontSize: '0.875rem',
                  color: '#111827'
                }}>
                  {item.units}
                </td>
                <td style={{
                  padding: '0.75rem 1rem',
                  textAlign: 'right',
                  fontSize: '0.875rem',
                  color: '#111827',
                  borderRight: '1px solid #e5e7eb'
                }}>
                  ${item.totalFee}
                </td>
                <td style={{
                  padding: '0.75rem 1rem',
                  textAlign: 'center',
                  fontSize: '0.875rem'
                }}>
                  <span style={{
                    backgroundColor: '#fee2e2',
                    color: '#991b1b',
                    padding: '0.25rem 0.5rem',
                    borderRadius: '4px',
                    fontSize: '0.75rem',
                    fontWeight: '600'
                  }}>
                    {item.status}
                  </span>
                </td>
                <td style={{
                  padding: '0.75rem 1rem',
                  textAlign: 'right',
                  fontSize: '0.875rem',
                  color: '#d1d5db'
                }}>
                  {item.charge !== null ? `$${item.charge}` : '--'}
                </td>
                <td style={{
                  padding: '0.75rem 1rem',
                  textAlign: 'right',
                  fontSize: '0.875rem',
                  color: '#d1d5db'
                }}>
                  {item.uninvoiced !== null ? `$${item.uninvoiced}` : '--'}
                </td>
                <td style={{
                  padding: '0.75rem 1rem',
                  textAlign: 'right',
                  fontSize: '0.875rem',
                  color: '#d1d5db'
                }}>
                  {item.paid !== null ? `$${item.paid}` : '--'}
                </td>
                <td style={{
                  padding: '0.75rem 1rem',
                  textAlign: 'right',
                  fontSize: '0.875rem',
                  color: '#d1d5db'
                }}>
                  {item.unpaid !== null ? `$${item.unpaid}` : '--'}
                </td>
              </tr>
            ))}

            {/* Empty State */}
            {!loading && data.length === 0 && (
              <tr>
                <td colSpan={11} style={{
                  padding: '2rem',
                  textAlign: 'center',
                  fontSize: '0.875rem',
                  color: '#d1d5db'
                }}>
                  No appointment status data available
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AppointmentStatusReport;
